import {
  jwtScopeAccessLevels,
  jwtScopeEntities,
  harvestiqDomain,
} from '@harvestiq/constants';
import {
  getInsureIqAuthServiceUrl,
  getInsureIqCdnUrl,
  isDevelopmentNodeEnv,
} from '@harvestiq/config';

// https://docs.datadoghq.com/real_user_monitoring/session_replay/browser/troubleshooting/#fonts-or-images-do-not-render-properly
export const DD_SESSION_REPLAY_DOMAIN = 'https://session-replay-datadoghq.com';
export const TRUSTED_ORIGINS = [
  getInsureIqAuthServiceUrl(),
  getInsureIqCdnUrl(),
  DD_SESSION_REPLAY_DOMAIN,
].filter(Boolean);

const apiVersion = 'v1';
export const apiRoot = `/api/${apiVersion}`;
export const uuidRegexp =
  /^[0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i;

// NOTE: see @harvestiq/utils PHONE_NUMBER_REGEX
export const phoneRegexp =
  /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;

export const phoneMask = '000-000-0000';
export const phoneLength = 10;
export const einLength = 9;
export const permissions = {
  PERMISSION_TO_ACCESS_LRP_QUOTE: 'ACCESS_LRP_QUOTE',
};

export const pdfInlineHeader = {
  ContentDisposition: 'inline',
  ContentType: 'application/pdf',
};

export const applicationS3Path = 'insurance/policies/apply/RMA';
export const rmaQuotesS3Path = 'insurance/quotes';

export const mediaTypeS3Path = {
  LGM: `${applicationS3Path}/LGM`,
  LRP: `${applicationS3Path}/LRP`,
  quotes: `${rmaQuotesS3Path}`,
};

export const gmt0timezone = 'Etc/GMT+0';
export const centralTimezone = 'America/Chicago';

// @todo move to external dictionary
export const textStrings = {
  emailNotAvailable: 'This email is not available.',
};

export const loginUnauthorizedMessage = 'Invalid email or password';
export const lgmSalesHelpText =
  'LGM is sold every Thursday when the latest data is published until 9:00 AM Central Time of the following day.';

export const marketIsClosedMsg =
  'Oops… the Product purchase window has closed. Click continue to notify an agent about your inquiry';
const minDate = new Date();
minDate.setTime(minDate.valueOf() - 100 * 365 * 24 * 60 * 60 * 1000); // hundred years
const maxDate = new Date();
maxDate.setTime(maxDate.valueOf() - 24 * 60 * 60 * 1000); // one day

export const defaultFilterValue = 'All';
export const emailTypes = ['personal', 'work'];
export const phoneTypes = {
  home: 'home',
  work: 'work',
  mobile: 'mobile',
};
export const timeFormats = {
  shortHoursMinutesAmPmTz: 'h:mma z',
  shortHoursMinutesAmPm: 'h:mm a',
  hoursMinutesSeconds: 'hh:mm:ss',
};
export const debounceTimeout = 500;
export const dateFormat = 'yyyy/MM/dd';
export const dateFormatTraditional = 'MM/dd/yy';
export const dateTimeFormatTraditional = `MM/dd/yy ${timeFormats.shortHoursMinutesAmPmTz}`;
export const lrpDateFormat = 'yyyy-MM-dd';
export const lgmDateFormat = 'MMM YYYY';
export const lgmDateFormatFns = 'MMM yyyy';
export const breakevenDateFormat = 'MMM yy';
export const dateFormatShort = 'yyyyMMdd';
export const defaultTimeZone = 'Europe/London';
export const defaultDateTimeFormat = `yyyy/MM/dd ${timeFormats.hoursMinutesSeconds}`;
export const breakevenCalculatorTooltipDateFormat = `${lrpDateFormat} ${timeFormats.shortHoursMinutesAmPm}`;
export const minDateOfBirth = minDate;
export const maxDateOfBirth = maxDate;
export const SECOND = 1000;
export const oneHourInSecs = 60 * 60; // 1 hour in seconds
export const saleWeightMultiplier = 0.98;
export const spouseType = {
  applicant: 'applicant',
  nonApplicant: 'non-applicant',
};
export const livestockGroups = {
  lgm: ['Swine', 'Cattle'],
  lrp: ['Swine', 'Feeder Cattle', 'Fed Cattle'],
};
export const livestockClasses = [
  ...new Set(Object.values(livestockGroups).flat()),
];
export const policyTypes = { lrp: 'LRP', lgm: 'LGM' };
export const policyOwnerTypes = {
  individual: 'Individual',
  business: 'Business',
};

export const lgmCacheKeys = {
  lgmPremiumDataCacheKey: 'lgm:premium-data',
  lgmQuoteDataCacheKey: 'lgm:quote-data',
  lgmExpectedDataBySedCacheKey: 'lgm:sed-expected-data',
  lgmExpectedGrossMarginQueryCacheKey: 'lgm:expected-gross-margin-query',
  lgmExpectedGrossMarginResultCacheKey: 'lgm:expected-gross-margin-result',
  lgmGuaranteeDataCacheKey: 'lgm:guarantee-data',
  lgmSalesEffectiveDatesPastYearCacheKey: 'lgm:sales-effective-dates-past-year',
  lgmPricesCacheKey: 'lgm:prices',
  lgmVsCmeCacheKey: 'lgm:lgm-vs-cme-data',
};

export const lrpCacheKeys = {
  lrpOptionsDailyCacheKey: 'lrp:options-daily',
  lrpRateDailyCacheKey: 'lrp:rate-daily',
  lrpPlotDataCacheKey: 'lrp:plot-data',
  lrpSalesEffectiveDatesPastYearCacheKey: 'lrp:sales-effective-dates-past-year',
  lrpMarketingDates: 'lrp:commodity-marketing-dates',
  lrpRateDailyDownloadCacheKey: 'lrp:rate-daily-download',
};

export const modelNames = {
  apiKey: 'API_Key',
  appSetting: 'App_Setting',
  aipSettings: 'AipSettings', // Model is named "AipSettings", but the table is named "Aip_Settings"
  coverage: 'Coverage',
  entityLocation: 'Entity_Location',
  entityOwner: 'Entity_Owner',
  entityPermission: 'Entity_Permission',
  faq: 'Faq',
  location: 'Location',
  media: 'Media',
  permission: 'Permission',
  policy: 'Policy',
  policyOwner: 'Policy_Owner',
  recordMedia: 'Record_Media',
  role: 'Role',
  signature: 'Signature',
  user: 'User',
  userProfile: 'User_Profile',
  userRole: 'User_Role',
};

export const currentYear = new Date().getFullYear();
export const longDateTimeFormat = 'PPP pp';

export const testUserEmail = 'test-user@stockguard.io';

export const monthIndicesByShortNames = {
  Jan: 0,
  Feb: 1,
  Mar: 2,
  Apr: 3,
  May: 4,
  Jun: 5,
  Jul: 6,
  Aug: 7,
  Sep: 8,
  Oct: 9,
  Nov: 10,
  Dec: 11,
};

export const paginationDefault = {
  page: 0,
  pageSize: 10,
};

export const orderTypes = {
  asc: 'asc',
  desc: 'desc',
};

export const jwtExpiresInSeconds = oneHourInSecs * 24;

export const defaultJwtScopes = [
  // Give explicit read and write access only the endpoints needed by 3rd party partners
  `${jwtScopeAccessLevels.read}:${jwtScopeEntities.quotes}`,
  `${jwtScopeAccessLevels.write}:${jwtScopeEntities.aipEvents}`,
];

export const referrerCookieName = 'sgref';
const techSupportUsername = ['tech-support'];
const serviceAccountUsername = ['service-account'];
const devTeamUsernames = ['eric', 'josh', 'marcel', 'andrews', 'mgreci'];
const adminTeamUsernames = ['dakota', 'nic', 'euweng', 'kierste', 'support'];
export const defaultAdminEmailsWhitelist = [
  ...adminTeamUsernames,
  ...devTeamUsernames,
  ...techSupportUsername,
].map((username) => {
  return `${username}@${harvestiqDomain}`;
});

const serviceAccountUsernames = isDevelopmentNodeEnv()
  ? serviceAccountUsername.concat(devTeamUsernames)
  : serviceAccountUsername;
const techSupportUsernames = isDevelopmentNodeEnv()
  ? techSupportUsername.concat(devTeamUsernames)
  : techSupportUsername;

export const serviceAccountEmails = serviceAccountUsernames.map(
  (username) => `${username}@${harvestiqDomain}`
);
// Admin Import
export const techSupportEmails = techSupportUsernames.map(
  (username) => `${username}@${harvestiqDomain}`
);

/**
 * Third party emails allowed to access internal environments
 */
export const allowedInternalEnv3rdPartyEmails = [];

export const MultiSelectAll = 'All';
export const MultiSelectClear = 'Clear All';
